import { useState, useEffect, useRef, useMemo } from "react";
import {
  Menu,
  Sun,
  Moon,
  ChevronDown,
  Search,
  Languages,
  Command,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NavigationPopup } from "@/components/ui/NavigationPopup";
import { BlogNavigationPopup } from "@/components/Blog/BlogNavigationPopup";
import { BrandLogo } from "@/components/BrandLogo";
import { projects } from "@/data/projects";
import { getBlogHomeUrl } from "@/lib/blogUrls";
import {
  SITE_LANGUAGES,
  getSiteLanguage,
  setSiteLanguage,
  type SiteLanguageCode,
} from "@/lib/language";
import { headerSearchCopy, tNav } from "@/lib/siteCopy";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { localizeServiceCategories } from "@/lib/localizeServices";
import { localizeProjects } from "@/lib/localizeProjects";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

type SearchHit = {
  label: string;
  description: string;
  path?: string;
  href?: string;
};

const LANGUAGES = SITE_LANGUAGES;

export function Header({
  onMobileMenuOpen,
  navItems,
  alwaysVisible: _alwaysVisible = false,
}: {
  onMobileMenuOpen: () => void;
  navItems: NavItem[];
  /** Kept for ServicesHeader compatibility - header is always sticky now. */
  alwaysVisible?: boolean;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDark, setIsDark] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const hideTimeout = useRef<number | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [language, setLanguage] = useState<(typeof LANGUAGES)[number]>(
    LANGUAGES[0],
  );
  const siteLang = useSiteLanguage();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const mobileSearchWrapRef = useRef<HTMLDivElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const langWrapRef = useRef<HTMLDivElement>(null);

  const primaryNavItems = useMemo(
    () => navItems.filter((item) => item.id !== "contact"),
    [navItems],
  );

  const searchIndex = useMemo<SearchHit[]>(() => {
    const categories = localizeServiceCategories(siteLang);
    const localizedProjects = localizeProjects(projects, siteLang);
    const serviceLabel = siteLang === "hi" ? "सेवा" : "Service";
    const categoryLabel =
      siteLang === "hi" ? "सेवा श्रेणी" : "Service category";

    const serviceHits: SearchHit[] = Object.values(categories).flatMap(
      (category) =>
        category.services.map((service) => ({
          label: service.title,
          description: `${category.title} · ${serviceLabel}`,
          path: `/services/${category.id}#${service.id}`,
        })),
    );

    const categoryHits: SearchHit[] = Object.values(categories).map(
      (category) => ({
        label: category.title,
        description: categoryLabel,
        path: `/services/${category.id}`,
      }),
    );

    return [
      {
        label: siteLang === "hi" ? "होम" : "Home",
        description:
          siteLang === "hi" ? "DevDotCom होमपेज" : "DevDotCom homepage",
        path: "/",
      },
      ...serviceHits,
      ...categoryHits,
      {
        label: siteLang === "hi" ? "कार्य" : "Work",
        description:
          siteLang === "hi" ? "चयनित केस स्टडी" : "Selected case studies",
        path: "/work",
      },
      {
        label: siteLang === "hi" ? "हमारे बारे में" : "About",
        description: siteLang === "hi" ? "टीम और कहानी" : "Team & story",
        path: "/about",
      },
      {
        label: siteLang === "hi" ? "करियर" : "Careers",
        description: siteLang === "hi" ? "टीम से जुड़ें" : "Join the team",
        path: "/careers",
      },
      {
        label: siteLang === "hi" ? "संपर्क" : "Contact",
        description:
          siteLang === "hi" ? "बातचीत शुरू करें" : "Start a conversation",
        path: "/contact",
      },
      {
        label: siteLang === "hi" ? "ब्लॉग" : "Blog",
        description:
          siteLang === "hi"
            ? "लेख और इंजीनियरिंग नोट्स"
            : "Articles & engineering notes",
        href: getBlogHomeUrl(),
      },
      {
        label: siteLang === "hi" ? "गोपनीयता नीति" : "Privacy Policy",
        description:
          siteLang === "hi"
            ? "हम आपके डेटा को कैसे संभालते हैं"
            : "How we handle your data",
        path: "/privacy",
      },
      {
        label: siteLang === "hi" ? "नियम और शर्तें" : "Terms & Conditions",
        description:
          siteLang === "hi"
            ? "वेबसाइट उपयोग की शर्तें"
            : "Website terms of use",
        path: "/terms",
      },
      ...localizedProjects.map((project) => ({
        label: project.title,
        description: `${project.industry} · ${project.year}`,
        path: `/work/${project.id}`,
      })),
    ];
  }, [siteLang]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return searchIndex
        .filter(
          (item) =>
            item.description.includes("· Service") ||
            item.description.includes("· सेवा"),
        )
        .slice(0, 8);
    }

    const rank = (item: SearchHit) => {
      const label = item.label.toLowerCase();
      let score = 50;
      if (label === q) score = 0;
      else if (label.startsWith(q)) score = 1;
      else if (label.includes(q)) score = 2;
      else if (item.description.toLowerCase().includes(q)) score = 3;

      // Prefer specific services over parent categories / pages
      if (item.description.includes("· Service")) score -= 0.5;
      else if (item.description === "Service category") score += 2;
      return score;
    };

    return searchIndex
      .filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q),
      )
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 8);
  }, [searchQuery, searchIndex]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "dark") {
        document.documentElement.classList.add("dark");
        setIsDark(true);
      } else {
        // Default light (also when unset or "light")
        document.documentElement.classList.remove("dark");
        setIsDark(false);
        if (stored !== "light") localStorage.setItem("theme", "light");
      }
    } catch {
      setIsDark(false);
    }

    const match = LANGUAGES.find((l) => l.code === getSiteLanguage());
    if (match) setLanguage(match);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
        window.setTimeout(() => searchInputRef.current?.focus(), 0);
      }
      if (e.key === "Escape") {
        setIsSearchOpen(false);
        setSearchQuery("");
        setLangOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDesktopSearch = searchWrapRef.current?.contains(target);
      const inMobileSearch = mobileSearchWrapRef.current?.contains(target);
      if (!inDesktopSearch && !inMobileSearch) {
        setIsSearchOpen(false);
      }
      if (langWrapRef.current && !langWrapRef.current.contains(target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const toggleTheme = () => {
    try {
      const next = !document.documentElement.classList.contains("dark");
      if (next) {
        document.documentElement.classList.add("dark");
        localStorage.setItem("theme", "dark");
      } else {
        document.documentElement.classList.remove("dark");
        localStorage.setItem("theme", "light");
      }
      setIsDark(next);
    } catch {
      /* ignore */
    }
  };

  const handleNavigation = (item: NavItem) => {
    scheduleHidePopupImmediate();

    if (item.type === "external" && item.href) {
      window.location.href = item.href;
    } else if (item.type === "route" && item.path) {
      navigate(item.path);
    } else if (item.type === "scroll" && item.id) {
      if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: item.id } });
      } else {
        scrollToSection(item.id);
      }
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (location.state && (location.state as { scrollTo?: string }).scrollTo) {
      const scrollTo = (location.state as { scrollTo: string }).scrollTo;
      setTimeout(() => {
        scrollToSection(scrollTo);
      }, 100);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const clearHideTimeout = () => {
    if (hideTimeout.current) {
      window.clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  };

  const showPopupFor = (id?: string) => {
    clearHideTimeout();
    if (!id) return;
    if (id === "services" || id === "blog") {
      setActivePopup(id);
      setIsPopupVisible(true);
    }
  };

  const scheduleHidePopup = () => {
    clearHideTimeout();
    hideTimeout.current = window.setTimeout(() => {
      setIsPopupVisible(false);
      setActivePopup(null);
    }, 500);
  };

  const scheduleHidePopupShort = () => {
    clearHideTimeout();
    hideTimeout.current = window.setTimeout(() => {
      setIsPopupVisible(false);
      setActivePopup(null);
    }, 120);
  };

  const scheduleHidePopupImmediate = () => {
    clearHideTimeout();
    setIsPopupVisible(false);
    setActivePopup(null);
  };

  const goToSearchHit = (hit: SearchHit) => {
    setSearchQuery("");
    setIsSearchOpen(false);
    if (hit.href) {
      window.location.href = hit.href;
      return;
    }
    if (!hit.path) return;
    const [pathname, hash] = hit.path.split("#");
    // Preserve hash so ServiceCategoryPage can open the service drawer
    navigate({
      pathname: pathname || "/",
      hash: hash ? `#${hash}` : undefined,
    });
  };

  const selectLanguage = (code: SiteLanguageCode) => {
    const next = LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0];
    setLanguage(next);
    setLangOpen(false);
    setSiteLanguage(next.code);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        isScrolled || isPopupVisible
          ? "border-b border-border bg-background/95 shadow-sm backdrop-blur-md"
          : "border-b border-transparent bg-background/80 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-4 lg:px-6">
        <div className="flex h-16 items-center gap-3 lg:gap-5">
          {/* Brand */}
          <button
            onClick={() => navigate("/")}
            onMouseEnter={() => scheduleHidePopupShort()}
            className="shrink-0 hover:opacity-80 transition-opacity"
            aria-label="DevDotCom home"
          >
            <BrandLogo priority />
          </button>

          {/* Primary nav - left-aligned with brand */}
          <nav className="hidden min-w-0 flex-1 items-center gap-0.5 lg:flex">
            {primaryNavItems.map((item) => {
              const hasPopup = item.id === "services" || item.id === "blog";
              const isActiveRoute =
                item.path &&
                (location.pathname === item.path ||
                  (item.path !== "/" &&
                    location.pathname.startsWith(`${item.path}/`)));
              return (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item)}
                  onMouseEnter={() => {
                    if (hasPopup) showPopupFor(item.id);
                    else scheduleHidePopupShort();
                  }}
                  onMouseLeave={() => {
                    if (hasPopup) scheduleHidePopup();
                  }}
                  className={`group flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors xl:px-3 ${
                    isActiveRoute
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                  }`}
                  aria-haspopup={hasPopup ? "true" : undefined}
                  aria-expanded={
                    hasPopup && isPopupVisible && activePopup === item.id
                      ? "true"
                      : "false"
                  }
                >
                  <span>{item.label}</span>
                  {hasPopup && (
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${
                        isPopupVisible && activePopup === item.id
                          ? "rotate-180"
                          : ""
                      } group-hover:rotate-180`}
                    />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right utilities */}
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            {/* Search */}
            <div ref={searchWrapRef} className="relative hidden md:block">
              <label className="sr-only" htmlFor="site-search">
                Search
              </label>
              <div
                className={`flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-muted-foreground transition-colors focus-within:border-foreground/30 focus-within:text-foreground ${
                  isSearchOpen ? "w-56 lg:w-64" : "w-44 lg:w-56"
                }`}
              >
                <Search className="h-4 w-4 shrink-0" />
                <input
                  id="site-search"
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsSearchOpen(true);
                  }}
                  onFocus={() => setIsSearchOpen(true)}
                  placeholder={headerSearchCopy[siteLang].placeholder}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  autoComplete="off"
                />
                <kbd className="hidden items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground lg:inline-flex">
                  <Command className="h-2.5 w-2.5" />K
                </kbd>
              </div>

              {isSearchOpen && searchResults.length > 0 && (
                <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-72 overflow-hidden rounded-xl border border-border bg-background shadow-lg lg:w-80">
                  <ul className="max-h-72 overflow-y-auto py-1">
                    {searchResults.map((hit) => (
                      <li key={`${hit.label}-${hit.path ?? hit.href}`}>
                        <button
                          type="button"
                          onClick={() => goToSearchHit(hit)}
                          className="flex w-full flex-col items-start gap-0.5 px-3.5 py-2.5 text-left hover:bg-muted"
                        >
                          <span className="text-sm font-medium text-foreground">
                            {hit.label}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {hit.description}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Language */}
            <div ref={langWrapRef} className="relative hidden sm:block">
              <button
                type="button"
                onClick={() => setLangOpen((open) => !open)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-label="Language"
              >
                <Languages className="h-4 w-4 text-muted-foreground" />
                <span>{language.short}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
                    langOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {langOpen && (
                <div
                  role="listbox"
                  className="absolute right-0 top-[calc(100%+0.5rem)] z-50 min-w-[9rem] overflow-hidden rounded-xl border border-border bg-background py-1 shadow-lg"
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      role="option"
                      aria-selected={language.code === lang.code}
                      onClick={() => selectLanguage(lang.code)}
                      className={`flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted ${
                        language.code === lang.code
                          ? "font-medium text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      <span>{lang.label}</span>
                      <span className="text-xs uppercase tracking-wide">
                        {lang.short}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme - visible on all breakpoints */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="h-9 w-9"
            >
              {isDark ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>

            {/* Contact CTA */}
            <Button
              size="sm"
              className="hidden h-9 rounded-lg px-4 text-sm font-medium sm:inline-flex"
              onClick={() => {
                scheduleHidePopupImmediate();
                navigate("/contact");
              }}
            >
              {tNav(siteLang, "Contact")}
            </Button>

            {/* Mobile search - left of sidebar menu */}
            <div ref={mobileSearchWrapRef} className="relative md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label="Search"
                aria-expanded={isSearchOpen}
                onClick={() => {
                  setIsSearchOpen((open) => {
                    const next = !open;
                    if (next) {
                      window.setTimeout(
                        () => mobileSearchInputRef.current?.focus(),
                        0,
                      );
                    } else {
                      setSearchQuery("");
                    }
                    return next;
                  });
                }}
              >
                <Search className="h-4 w-4" />
              </Button>

              {isSearchOpen && (
                <div className="fixed left-3 right-3 top-[4.25rem] z-50 overflow-hidden rounded-xl border border-border bg-background shadow-lg sm:left-auto sm:right-3 sm:w-[22rem]">
                  <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <input
                      ref={mobileSearchInputRef}
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={headerSearchCopy[siteLang].placeholder}
                      className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                      autoComplete="off"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <ul className="max-h-[min(60vh,22rem)] overflow-y-auto py-1">
                      {searchResults.map((hit) => (
                        <li key={`m-${hit.label}-${hit.path ?? hit.href}`}>
                          <button
                            type="button"
                            onClick={() => goToSearchHit(hit)}
                            className="flex w-full flex-col items-start gap-0.5 px-3.5 py-2.5 text-left hover:bg-muted"
                          >
                            <span className="text-sm font-medium text-foreground">
                              {hit.label}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {hit.description}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Mobile menu */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 lg:hidden"
              onClick={onMobileMenuOpen}
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <NavigationPopup
        isVisible={isPopupVisible && activePopup !== "blog"}
        activeItem={activePopup}
        onMouseEnter={() => clearHideTimeout()}
        onMouseLeave={() => scheduleHidePopupImmediate()}
        closePopup={() => scheduleHidePopupImmediate()}
      />

      <BlogNavigationPopup
        isVisible={isPopupVisible && activePopup === "blog"}
        activeItem={activePopup}
        onMouseEnter={() => clearHideTimeout()}
        onMouseLeave={() => scheduleHidePopupImmediate()}
        closePopup={() => scheduleHidePopupImmediate()}
      />
    </header>
  );
}
